import { snakeCase } from 'lodash';
import { CardsMarketplaceOffer } from 'src/types/marketplace';
import {
  HowLongToPayOffDebt,
  PrimaryGoal,
  AirlineChoice,
  CreditScore,
  CurrentBalanceCompany,
  HotelChoice,
} from 'src/types/inputs';

import {
  Input,
  LegacyAirlineChoice,
  LegacyCardDeposit,
  LegacyCreditScore,
  LegacyCurrentBalanceCompany,
  LegacyHotelChoice,
  LegacyHowLongToPayOffDebt,
  LegacyPrimaryGoal,
} from 'src/lib/constants';
import { Flow } from 'src/lib/flows/flow-definition';
import { Screen } from 'src/lib/flows/screens';
import { InputValue, InputsReducer } from 'src/redux/reducers/inputs';
import {
  Cookie,
  ProductType,
  EntityName,
  EventType,
  Monetizing,
  LinkType,
  FinishType,
  FormName,
} from './constants';
import { Recommendations } from 'src/redux/reducers/recommendations';
import { EventPayload } from './Events';

export function getCookie(cookieName: string) {
  if (typeof document !== 'undefined') {
    for (const cookie of document.cookie.split(/;\s?/)) {
      const [key, value] = cookie.split('=');
      if (key === cookieName) {
        return decodeURIComponent(value);
      }
    }
  }
  return ''; // TODO probably not appropriate return value
}

export const getMessages = (
  eventType: EventType,
  payload: Record<string, any>
) => [
  {
    exchangeId: 'ANALYTIC_PIPELINE_MOUTH',
    type: eventType,
    payload: {
      ...payload,
      frame: {
        deployable_environment: process.env.NEXT_PUBLIC_DEPLOYABLE_ENV,
        deployable_name: process.env.NEXT_PUBLIC_CLIENT_ID,
        deployable_version: '1.0.0',
        device_id: getCookie(Cookie.DeviceId),
        visitor_identity_assertion_token: getCookie(
          'visitor_identity_assertion_token'
        ),
        member_identity_assertion_token: getCookie(
          'member_identity_assertion_token'
        ),
        event_occurred_msec_ago: 0,
        event_seqno: 0,
      },
    },
  },
];

const VERSION_1_MAP: Partial<Record<Input, any>> = {
  [Input.PrimaryGoal]: {
    [LegacyPrimaryGoal.BigPurchase]: PrimaryGoal.BigPurchase,
    [LegacyPrimaryGoal.BuildCredit]: PrimaryGoal.BuildCredit,
    [LegacyPrimaryGoal.CashBack]: PrimaryGoal.CashBack,
    [LegacyPrimaryGoal.Debt]: PrimaryGoal.Debt,
    [LegacyPrimaryGoal.Travel]: PrimaryGoal.Travel,
  },
  [Input.CreditScore]: {
    [LegacyCreditScore.Average]: CreditScore.Average,
    [LegacyCreditScore.Excellent]: CreditScore.Excellent,
    [LegacyCreditScore.Good]: CreditScore.Good,
    [LegacyCreditScore.Poor]: CreditScore.Poor,
    [LegacyCreditScore.LimitedCreditHistory]: CreditScore.LimitedCreditHistory,
  },
  [Input.AirlineChoice]: {
    [LegacyAirlineChoice.AerLingus]: AirlineChoice.AerLingus,
    [LegacyAirlineChoice.Aeroplan]: AirlineChoice.Aeroplan,
    [LegacyAirlineChoice.AmericanAirlines]: AirlineChoice.AmericanAirlines,
    [LegacyAirlineChoice.AlaskaAirlines]: AirlineChoice.AlaskaAirlines,
    [LegacyAirlineChoice.BritishAirways]: AirlineChoice.BritishAirways,
    [LegacyAirlineChoice.Delta]: AirlineChoice.Delta,
    [LegacyAirlineChoice.Jetblue]: AirlineChoice.Jetblue,
    [LegacyAirlineChoice.Iberia]: AirlineChoice.Iberia,
    [LegacyAirlineChoice.SouthwestAirlines]: AirlineChoice.SouthwestAirlines,
    [LegacyAirlineChoice.UnitedAirlines]: AirlineChoice.UnitedAirlines,
    [LegacyAirlineChoice.Other]: AirlineChoice.Other,
  },
  [Input.HotelChoice]: {
    [LegacyHotelChoice.Hilton]: HotelChoice.Hilton,
    [LegacyHotelChoice.Hyatt]: HotelChoice.Hyatt,
    [LegacyHotelChoice.IHG]: HotelChoice.Ihg,
    [LegacyHotelChoice.Marriot]: HotelChoice.Marriott,
    [LegacyHotelChoice.Radisson]: HotelChoice.Radisson,
    [LegacyHotelChoice.Wyndham]: HotelChoice.Wyndham,
    [LegacyHotelChoice.Other]: HotelChoice.Other,
  },
  [Input.HowLongToPayOffDebt]: {
    [LegacyHowLongToPayOffDebt.LessThan18Months]:
      HowLongToPayOffDebt.LessThan18Months,
    [LegacyHowLongToPayOffDebt.MoreThan18Months]:
      HowLongToPayOffDebt.MoreThan18Months,
  },
  [Input.CurrentBalanceCompany]: {
    [LegacyCurrentBalanceCompany.BankOfAmerica]:
      CurrentBalanceCompany.BankOfAmerica,
    [LegacyCurrentBalanceCompany.CapitalOne]: CurrentBalanceCompany.CapitalOne,
    [LegacyCurrentBalanceCompany.Chase]: CurrentBalanceCompany.Chase,
    [LegacyCurrentBalanceCompany.Citi]: CurrentBalanceCompany.Citibank,
    [LegacyCurrentBalanceCompany.Discover]: CurrentBalanceCompany.Discover,
    [LegacyCurrentBalanceCompany.WellsFargo]: CurrentBalanceCompany.WellsFargo,
    [LegacyCurrentBalanceCompany.UsBank]: CurrentBalanceCompany.UsBank,
    [LegacyCurrentBalanceCompany.Other]: CurrentBalanceCompany.Other,
  },
  // FIXME get product buy in on this mapping:
  [Input.CardDeposit]: {
    [LegacyCardDeposit.LessThan200]: false,
    [LegacyCardDeposit.Between200And300]: true,
    [LegacyCardDeposit.MoreThan300]: true,
  },
  // Boolean Input types do not need conversion!
  [Input.AnnualFees]: null,
  [Input.CollegeStudent]: null,
  [Input.IsBuildingCredit]: null,
  [Input.InternationalTravel]: null,
  [Input.IsDebtCreditCard]: null,
  [Input.SpecificAirlineOrHotel]: null,
  [Input.SpendingCategories]: null,
  //new Big purchase flow
  [Input.IntroAPROrRewards]: null,
  [Input.PurchaseSpend]: null,
  [Input.CashBackBigPurchase]: null,
  [Input.PurchaseSpendingCategories]: null,
  // New CashBack flow
  [Input.CashBackPreference]: null,
  [Input.ManagingCategories]: null,
  [Input.MonthlySpend]: null,
  [Input.LuxuryPerks]: null,
  [Input.RewardsAndPerks]: null,
  [Input.SecurityDeposit]: null,
  [Input.TypeOfDebt]: undefined,
  // Build credit
  [Input.StudentSpendingCategories]: null,
};

export const getLegacyTrackingValue = (inputName: Input, value: any): any =>
  Object.keys(VERSION_1_MAP[inputName] ?? {}).find(
    (key) => VERSION_1_MAP[inputName][key] === value
  );

export const boolToText = (value: boolean): string => (value ? 'yes' : 'no');

export const getQuickEditSectionName = (inputs: InputsReducer) =>
  `cc_recs_${getLegacyTrackingValue(Input.PrimaryGoal, inputs.primaryGoal)}`;

export const getResultsSectionName = (inputs: InputsReducer) =>
  `cc_match_results_${getLegacyTrackingValue(
    Input.PrimaryGoal,
    inputs.primaryGoal
  )}`;

export const getProductImpressionPayload = (
  marketplaceEntity: CardsMarketplaceOffer,
  inputs: InputsReducer,
  impressionId: string,
  elementPosition: number
): EventPayload<EventType.ProductImpression> => ({
  entity_name: EntityName.MatchResultsProduct,
  section_name: getResultsSectionName(inputs),
  section_position: 2,
  element_position: elementPosition,
  marketplace_product_type: ProductType.CreditCards,
  marketplace_institution_id: marketplaceEntity?.product?.institution?.id,
  marketplace_product_id: marketplaceEntity?.product?.id,
  marketplace_offer_id: marketplaceEntity?.id,
  monetizable: marketplaceEntity.details.is_monetizing
    ? Monetizing.YesAssumed
    : Monetizing.No,
  destination_url: marketplaceEntity?.derived_details?.apply_link,
  page_number: null,
  driver: null,
  parent_impression_id: impressionId,
});

export const getMarketplaceOffersPayload = (
  recommendations: Recommendations,
  inputs: InputsReducer,
  impressionId: string,
  formSubmitId?: string
): EventPayload<EventType.RecsOffersImpression> => ({
  additional_attributes: {
    primary_goal: getLegacyTrackingValue(Input.PrimaryGoal, inputs.primaryGoal),
    form_submit_id: formSubmitId,
    marketplace_product_type: ProductType.CreditCards,
    marketplace_offers_count: recommendations?.length ?? 0,
    marketplace_offer_ids:
      recommendations?.map((cc: any) => cc?.offer?.id) ?? [],
  },
  section_name: getResultsSectionName(inputs),
  section_position: 2,
  parent_impression_id: impressionId,
});

export const getApplyLinkParameters = (
  offer: CardsMarketplaceOffer,
  {
    elementPosition,
    impressionId,
    linkType,
    navigationId,
    sectionName,
    sectionPosition,
  }: GetApplyLinkAnalytics
): Record<string, string> => ({
  source_url: window?.location?.href,
  impression_id: impressionId,
  finish_type: FinishType.ExternalApplication,
  link_type: linkType,
  header_pageViewId: navigationId,
  product_position: `${elementPosition}`,
  section_name: sectionName,
  section_position: `${sectionPosition}`,
  monetizable: offer.details.is_monetizing
    ? Monetizing.YesAssumed
    : Monetizing.No,
});

export type GetApplyLinkAnalytics = {
  elementPosition: number;
  impressionId: string;
  linkType: LinkType;
  navigationId: string;
  sectionName: string;
  sectionPosition: number;
};
export const getApplyLink = (
  offer: CardsMarketplaceOffer,
  analytics: GetApplyLinkAnalytics
) => {
  if (!offer.derived_details.apply_link) {
    return null;
  }

  const params = new URLSearchParams(getApplyLinkParameters(offer, analytics));

  return `${offer.derived_details.apply_link}&${params}`;
};

export const stringifyAnswerValue = (answerValue: InputValue): string => {
  if (typeof answerValue === 'boolean') {
    return boolToText(answerValue);
  }
  if (typeof answerValue === 'number') {
    return `${answerValue}`;
  }

  if (Array.isArray(answerValue)) {
    if (typeof answerValue[0] === 'string') {
      const multiSelectAnswers = [...answerValue];
      multiSelectAnswers.sort();
      return multiSelectAnswers.join(',');
    } else {
      return JSON.stringify(answerValue);
    }
  }

  return answerValue ?? '';
};

export const updateQuizAnswerAnalytics = (
  answerValue: InputValue,
  inputName: Input,
  pageNumber: number,
  wasPrefilled = false
) => ({
  eventType: EventType.ShoppingGenericUpdated,
  payload: {
    form_name: FormName.Flow,
    page_number: pageNumber,
    total_pages: undefined,
    is_valid: true,
    validation_failure_message: '',
    was_prefilled: wasPrefilled,
    additional_attributes: {
      answer_value: stringifyAnswerValue(answerValue),
      question_name: inputName,
    },
  },
});

export const getFlowSectionName = (
  screenName: Screen,
  primaryGoal?: PrimaryGoal
): string => {
  if (primaryGoal && screenName !== Screen.PrimaryGoal) {
    return `cc_curated_offers_${snakeCase(primaryGoal)}_${snakeCase(
      screenName
    )}`;
  }

  return `cc_curated_offers_${snakeCase(screenName)}`;
};

const parseInputToScreen = (input: Input, flow: Flow): Screen => {
  return Object.keys(flow).find((screen) =>
    flow?.[screen]?.inputs?.includes(input)
  ) as Screen;
};

const sortMultiSelectValues = (answerValue: InputValue) => {
  if (Array.isArray(answerValue)) {
    const multiSelectAnswers = [...answerValue];
    multiSelectAnswers.sort();
    return multiSelectAnswers;
  }

  return answerValue;
};

export const getRelevantInputs = (
  inputs: InputsReducer,
  visitedScreens: Screen[],
  activeFlow: Flow
): InputsReducer => {
  const inputEntries = Object.entries(inputs);
  return inputEntries.reduce(
    (acc, [key, value]) =>
      visitedScreens.includes(parseInputToScreen(key as Input, activeFlow))
        ? { ...acc, [key]: sortMultiSelectValues(value) }
        : acc,
    {}
  );
};
interface TransformApplyLinkProps {
  applyLink: string;
  shoppingFinishedId: string;
}

export const transformApplyLink = ({
  applyLink,
  shoppingFinishedId,
}: TransformApplyLinkProps) => {
  const url = new URL(applyLink);
  url.searchParams.append('shopping_finish_id', shoppingFinishedId);
  return url.toString();
};

export const gtmPushToDataLayer = (values: any) => {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(values);
};

export const mappedCreditScore = (creditScore: CreditScore) => {
  switch (creditScore) {
    case CreditScore.Average:
      return 'FAIR';
    case CreditScore.LimitedCreditHistory:
    case CreditScore.Poor:
      return 'BAD';
    default:
      return creditScore;
  }
};

export const mappedCardGoal = (cardGoal: PrimaryGoal) => {
  switch (cardGoal) {
    case PrimaryGoal.BigPurchase:
      return 'MAJORPURCHASE';
    case PrimaryGoal.BuildCredit:
      return 'BUILDCREDIT';
    case PrimaryGoal.Debt:
      return 'DEBTPAYOFF';
    case PrimaryGoal.CashBack:
      return 'CASHBACK';
    default:
      return cardGoal;
  }
};
